import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  Suspense,
  type ReactNode,
} from 'react';

import { makeDialog, DialogV2, Inset } from '@cast/design-system';
import { lazyRetry } from '@cast/utils';

import { useGlobalSearchHotkey } from './_hooks/useGlobalSearchHotkey';
import { HEADER_HEIGHT } from '../../layout/layoutConstants';
import { SuspenseLoader } from 'components/loaders';

const LazyGlobalSearchDialog = lazyRetry(() =>
  import('./_components/GlobalSearchDialog').then((m) => ({
    default: m.GlobalSearchDialog,
  }))
);

const GlobalSearchDialogFallback = () => (
  <DialogV2
    title={null}
    hideCloseButton
    testId="global-search-dialog-loading"
    slotProps={{
      root: {
        sx: {
          position: 'fixed',
          top: `${HEADER_HEIGHT}px`,
          left: 0,
          right: 0,
          marginLeft: 'auto',
          marginRight: 'auto',
        },
      },
    }}
  >
    <Inset testId="global-search-loading" height={420}>
      <SuspenseLoader />
    </Inset>
  </DialogV2>
);

const GlobalSearchDialogWithSuspense = () => (
  <Suspense fallback={<GlobalSearchDialogFallback />}>
    <LazyGlobalSearchDialog />
  </Suspense>
);

type GlobalSearchContextValue = {
  open: () => void;
};

const GlobalSearchContext = createContext<GlobalSearchContextValue>({
  open: () => {},
});

export const useGlobalSearch = () => useContext(GlobalSearchContext);

const useSearchDialog = makeDialog(GlobalSearchDialogWithSuspense, {
  disableAutoDestroy: false,
  closeOnOverlayClick: true,
});

type GlobalSearchProviderProps = {
  children?: ReactNode;
};

export const GlobalSearchProvider = ({
  children,
}: GlobalSearchProviderProps) => {
  // Track whether the dialog is already open so repeated ⌘K presses (don't stack multiple instances).
  const isOpenRef = useRef(false);

  const { openDialog } = useSearchDialog({
    onClose: () => {
      isOpenRef.current = false;
    },
  });

  const handleOpen = useCallback(() => {
    if (isOpenRef.current) return;
    isOpenRef.current = true;
    openDialog();
  }, [openDialog]);

  useGlobalSearchHotkey(handleOpen);

  const contextValue = useMemo(() => ({ open: handleOpen }), [handleOpen]);

  return (
    <GlobalSearchContext.Provider value={contextValue}>
      {children}
    </GlobalSearchContext.Provider>
  );
};
